import { DirectorResponse } from '@/types/director.types';
import { ShotData } from '@/types/shot.types';
import { cameraPresets } from '@/data/cameraPresets';
import { mockShots } from '@/data/mockShots';

/**
 * Script entry defining when to return specific data
 */
export interface ScriptEntry {
  time: number; // milliseconds from start
  cameraPresetKey: string | null; // key in cameraPresets
  shotIds: string[]; // IDs of shots to include
}

/**
 * Mock implementation of the Dynamic Director API
 * Returns predetermined responses based on a script timeline
 */
export class MockDirectorService {
  private startTime: number | null = null;
  private lastReturnedIndex: number = -1;
  private script: ScriptEntry[];
  private shots: ShotData[];

  // Default script for backwards compatibility
  private static readonly DEFAULT_SCRIPT: ScriptEntry[] = [
    {
      time: 0,
      cameraPresetKey: 'hole1',
      shotIds: ['shot-1'],
    },
    {
      time: 3000,
      cameraPresetKey: 'hole2',
      shotIds: ['shot-2'],
    },
    {
      time: 5000,
      cameraPresetKey: 'hole2', // Same camera, new shot
      shotIds: ['shot-3'],
    },
  ];

  /**
   * Create a new MockDirectorService
   * @param script - Timeline script (defaults to built-in script)
   * @param shots - Shot data array (defaults to mockShots)
   */
  constructor(script?: ScriptEntry[], shots?: ShotData[]) {
    this.script = script || MockDirectorService.DEFAULT_SCRIPT;
    this.shots = shots || mockShots;
  }

  /**
   * Get current view from the Dynamic Director
   * Simulates: GET /api/director/{eventId}/current-view
   */
  public getCurrentView(): DirectorResponse {
    // Initialize timer on first poll to avoid race conditions
    if (this.startTime === null) {
      this.startTime = Date.now();
    }

    const elapsed = Date.now() - this.startTime;

    // Find the next script entry that should be returned
    for (let i = this.lastReturnedIndex + 1; i < this.script.length; i++) {
      const entry = this.script[i];

      if (elapsed >= entry.time) {
        // This entry should be returned now
        this.lastReturnedIndex = i;

        return {
          cameraPreset: entry.cameraPresetKey
            ? cameraPresets[entry.cameraPresetKey]
            : null,
          shots: entry.shotIds
            .map(id => this.shots.find(shot => shot.id === id))
            .filter((shot): shot is NonNullable<typeof shot> => shot !== undefined),
          timestamp: Date.now(),
        };
      }
    }

    // No new data to return
    return {
      cameraPreset: null,
      shots: [],
      timestamp: Date.now(),
    };
  }

  /**
   * Reset the service to initial state (useful for testing)
   */
  public reset(): void {
    this.startTime = null;
    this.lastReturnedIndex = -1;
  }

  /**
   * Get remaining script entries (for debugging)
   */
  public getRemainingScript(): ScriptEntry[] {
    return this.script.slice(this.lastReturnedIndex + 1);
  }
}
